// Report Generator types
import { Chart, ChartType, ICubicData } from "store/types";

// ============================================
// Card Data Types
// ============================================

export interface TableColumn {
  key: string;
  title: string;
}

export interface TableRow {
  title?: string;
  valueData?: Record<string, number | null>;
  children?: TableRow[];
  [key: string]: string | number | null | Record<string, number | null> | TableRow[] | undefined;
}

export interface DataTableCardData {
  columns: Array<string | TableColumn>;
  data: TableRow[];
}

export interface SummaryCardData {
  parameter?: string;
  total?: string | number;
  average?: string | number;
  min?: string | number;
  max?: string | number;
  value?: string | number;
  measureName?: string;
  title?: string;
}

export type KPICardData = Partial<ICubicData>;

export interface TextCardData {
  content: string | null;
}

export interface AICardData {
  answer?: string;
}

// Union type for all card data
export type CardData = Chart | DataTableCardData | SummaryCardData | KPICardData | TextCardData | AICardData;

// ============================================
// View & Canvas Types
// ============================================

export type ReportViewType = "chart" | "data_table" | "summary_card" | "kpi_card" | "text_card" | "ai_card";

export interface CustomConfig {
  // Chart fields
  chartType?: ChartType;
  graphDataFormation?: Record<
    string,
    {
      measure: string;
      groupBy: string;
      chartType: ChartType;
    }
  >;

  // Data table fields (API payload format)
  measures?: string[];
  calculatedFields?: string[];
  characteristics?: string[];
  columns?: Array<{ aggregation: string }>;
  groupBy?: string[][];
  rawGroupBy?: string[];
  columnBy?: "measure";
  rowBy?: "deal" | "investor" | "measure";
  indexName?: string;

  // KPI fields
  cubics?: Record<string, { title: string; filter: string }>;

  // Summary fields
  parameter?: string;

  // Text card fields
  content?: string;

  // AI card fields
  question?: string;
  answer?: string;

  // Fetched data
  chartData?: CardData;
}

export interface ViewDefinition {
  id: string;
  type: ReportViewType;
  title: string;
  description: string;
  config: Partial<CustomConfig>;
  previewData?: CardData;
}

export interface CanvasView {
  id: string;
  type: ReportViewType;
  title: string;
  gridCol: number;
  gridRow: number;
  gridSpan: number;
  gridRowSpan: number;
  data?: CardData;
  pageIndex: number;
  customConfig?: CustomConfig;
}

export interface PageMeta {
  backgroundOpacity?: number;
}

export interface DragPreview {
  col: number;
  row: number;
  span: number;
  isValid: boolean;
}

export interface ResizingCard {
  id: string;
  startSpan: number;
  startRowSpan: number;
  startX: number;
  startY: number;
}

// ============================================
// Image Search Types
// ============================================

export interface SearchImageResult {
  thumb: string;
  regular: string;
  full: string;
}

// ============================================
// Template Types (API)
// ============================================

export interface ReportGeneratorTemplateListItem {
  id: number;
  name: string;
  description?: string;
  created_at: string;
}

export interface ReportGeneratorTemplateFile {
  id: number;
  name: string;
  file_url: string;
  type: "logo" | "background";
}

export interface ReportGeneratorTemplate {
  id: number;
  name: string;
  description?: string | null;
  deal_id?: number | null;
  report_title: string | null;
  content: ReportGeneratorSavedCard[][] | string | null;
  content_metadata: PageMeta | string | null;
  files: ReportGeneratorTemplateFile[];
  created_at: string;
}

export interface ReportGeneratorTemplateSavePayload {
  name: string;
  description?: string;
  deal_id?: number;
  report_title?: string;
  content?: string;
  content_metadata?: string;
  logo?: File;
  logo_url?: string;
  background?: File;
  background_url?: string;
}

export interface ReportGeneratorSavedCard {
  type: string;
  title: string;
  gridCol: number;
  gridRow: number;
  gridSpan: number;
  gridRowSpan: number;
  customConfig?: Record<string, unknown>;
}

// ============================================
// Constants
// ============================================

export const GRID_CONSTANTS = {
  CANVAS_WIDTH: 642,
  CANVAS_HEIGHT: 971,
  GRID_COLUMNS: 12,
  GAP: 12,
  GRID_CELL_HEIGHT: 60,
  MARGIN: 20,
  HEADER_HEIGHT: 80,
  FOOTER_HEIGHT: 40,
  ALIGN_SNAP_TOLERANCE: 4
} as const;

export const DEFAULT_VIEW_ITEMS: ViewDefinition[] = [
  { id: "chart", type: "chart", title: "Graphs / Charts", description: "Visualize data", config: {} },
  { id: "table", type: "data_table", title: "Data Table", description: "Display tabular data", config: {} },
  { id: "summary", type: "summary_card", title: "Summary Card", description: "Summary statistics", config: {} },
  { id: "kpi", type: "kpi_card", title: "KPI Card", description: "Key metrics display", config: {} },
  { id: "text", type: "text_card", title: "Text Card", description: "Custom text content", config: {} },
  { id: "ai", type: "ai_card", title: "AI Card", description: "AI-generated content", config: {} }
];
